import type { GameState, PlayerStats } from './types';
import { subscribe, unsubscribe } from './event-log';

/**
 * Zero-valued PlayerStats used as a fallback for unknown players.
 * Frozen to prevent accidental mutation of the template.
 */
const ZERO_STATS: Readonly<PlayerStats> = Object.freeze({
  keysGained: 0,
  keysLost: 0,
  minigamesWon: 0,
  minigamesLost: 0,
  damageDealt: 0,
  damageReceived: 0,
  tilesTraveled: 0,
  itemsUsed: 0,
});

/**
 * Initialize PlayerStats for every player in state.turnOrder that does not
 * yet have an entry in state.stats.
 *
 * Idempotent: safe to call multiple times; existing entries are left untouched.
 *
 * NOTE: createGameState (state-factory.ts) already pre-populates state.stats
 * for all players. This function is a safety net for tests and scenarios where
 * a GameState is built manually without invoking createGameState.
 *
 * @param state - Game state to mutate.
 */
export function initializePlayerStats(state: GameState): void {
  for (const playerId of state.turnOrder) {
    if (!state.stats.has(playerId)) {
      state.stats.set(playerId, { ...ZERO_STATS });
    } else {
      normalizePlayerStats(state.stats.get(playerId)!);
    }
  }
}

export function ensurePlayerStats(state: GameState, playerId: string): PlayerStats {
  const existing = state.stats.get(playerId);
  if (existing != null) return normalizePlayerStats(existing);
  const created: PlayerStats = { ...ZERO_STATS };
  state.stats.set(playerId, created);
  return created;
}

function normalizePlayerStats(stats: PlayerStats): PlayerStats {
  stats.keysGained ??= 0;
  stats.keysLost ??= 0;
  stats.minigamesWon ??= 0;
  stats.minigamesLost ??= 0;
  stats.damageDealt ??= 0;
  stats.damageReceived ??= 0;
  stats.tilesTraveled ??= 0;
  stats.itemsUsed ??= 0;
  return stats;
}

/**
 * Return the PlayerStats for the given player.
 *
 * - If the player exists in state.stats, returns the live reference (mutations
 *   to the returned object are reflected in state.stats).
 * - If the player is unknown, returns a fresh all-zero object WITHOUT mutating
 *   state.stats.
 *
 * @param state    - Game state to query.
 * @param playerId - ID of the player to look up.
 */
export function getPlayerStats(state: GameState, playerId: string): PlayerStats {
  if (state.players.has(playerId)) return ensurePlayerStats(state, playerId);
  const stats = state.stats.get(playerId);
  if (!stats) return { ...ZERO_STATS };
  return normalizePlayerStats(stats);
}

/**
 * Create and register a lightweight stats subscriber for the given game state.
 *
 * Design intent:
 * - Keys and damage stats are ALREADY updated directly by modifyKeys /
 *   applyDamage in economy.ts / damage.ts. This subscriber must NOT touch
 *   those fields to avoid double-counting.
 * - The subscriber is a placeholder for future minigame_result events (Plan 8+)
 *   which will update minigamesWon / minigamesLost here.
 *
 * @param state - Game state the subscriber will act on (captured by closure).
 * @returns An opaque subscriber ID that can be passed to destroyStatsSubscriber.
 */
export function createStatsSubscriber(state: GameState): string {
  // The `state` parameter is retained for future use when minigame events
  // need to mutate state.stats. The `void state` line keeps TS/lint quiet.
  void state;

  return subscribe((event) => {
    // Placeholder — future minigame_result events will update
    // minigamesWon / minigamesLost here once Plan 8 introduces them.
    // Keys and damage stats are intentionally NOT handled here
    // (they are updated in modifyKeys / applyDamage to avoid double-counting).
    void event;
  });
}

/**
 * Unregister a stats subscriber by its ID.
 * Safe to call multiple times (unsubscribing an unknown ID is a no-op).
 *
 * @param subscriberId - ID returned by createStatsSubscriber.
 */
export function destroyStatsSubscriber(subscriberId: string): void {
  unsubscribe(subscriberId);
}
